export type SlashCommand = {
  name: string;
  label: string;
  description: string;
  hint: string;
  /** Shown in the empty-chat command list. */
  highlight: boolean;
  /** When false, Enter sends the command with no extra text. */
  requiresArgs: boolean;
  expand: (args: string) => string;
};

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    name: 'quote',
    label: 'Quote',
    description: 'In-stock watches, one line each',
    hint: 'Model, dial, bracelet',
    highlight: true,
    requiresArgs: true,
    expand: (args) =>
      `Quote (in-stock only — reply with exactly: Model · Dial · Bracelet · Year · WS price, one line per watch, no analysis): ${args}`,
  },
  {
    name: 'fill',
    label: 'Fill',
    description: 'WTB post to filling groups',
    hint: 'Press Enter to run',
    highlight: true,
    requiresArgs: false,
    expand: (args) =>
      args
        ? `Send WTB WhatsApp for need-to-fill orders matching: ${args}. List every line and the count, then wait for confirmation before sending.`
        : 'Send WTB WhatsApp for all need-to-fill orders. List every line and the count, then wait for confirmation before sending.',
  },
  {
    name: 'flags',
    label: 'Flags',
    description: 'Inspection issues on unsold stock',
    hint: 'Press Enter to run',
    highlight: false,
    requiresArgs: false,
    expand: (args) =>
      args
        ? `Inspection recap for current unsold stock with Yellow or Red issues matching: ${args}.`
        : 'Inspection recap for current unsold stock with Yellow or Red inspection issues.',
  },
  {
    name: 'proforma',
    label: 'Proforma',
    description: 'Quote PDF to a customer',
    hint: 'Customer and watch',
    highlight: true,
    requiresArgs: true,
    expand: (args) => `Send a proforma invoice: ${args}`,
  },
  {
    name: 'label',
    label: 'Label',
    description: 'Standalone incoming shipment',
    hint: 'Seller and watch',
    highlight: true,
    requiresArgs: true,
    expand: (args) =>
      `Create a standalone incoming shipping label, not for an order: ${args}. Do not look up or attach an outgoing order. Direction is incoming. Omit outgoingOrderId. Recipient is TLC. Dial color and bracelet are not required — do not ask for them if they were not mentioned.`,
  },
  {
    name: 'sold',
    label: 'Sold',
    description: 'Create an outgoing order',
    hint: 'Type and watch',
    highlight: true,
    requiresArgs: true,
    expand: (args) =>
      `Create an outgoing order: ${args}. Ask for anything required that is missing. Dial color and bracelet are not required — do not ask for them if they were not mentioned. Recap the order, then wait for confirmation before creating.`,
  },
  {
    name: 'approvals',
    label: 'Approvals',
    description: 'OK to ship and labels',
    hint: 'Press Enter to run',
    highlight: false,
    requiresArgs: false,
    expand: (args) =>
      args
        ? `Review the Approvals queue matching: ${args}. List LIVE Retail, Pickup, and Wholesale orders that still need OK to ship and/or a shipping label. Recap, then wait for confirmation before changing anything.`
        : 'Review the Approvals queue. List every LIVE Retail, Pickup, and Wholesale order that still needs OK to ship and/or a shipping label. Recap, then wait for confirmation before changing anything.',
  },
  {
    name: 'docusign',
    label: 'DocuSign',
    description: 'Envelope for signature',
    hint: 'Template and recipient',
    highlight: false,
    requiresArgs: true,
    expand: (args) => `Send a DocuSign: ${args}`,
  },
  {
    name: 'pr',
    label: 'Pull request',
    description: 'GitHub pull request, not merged',
    hint: 'What to change',
    highlight: false,
    requiresArgs: true,
    expand: (args) => `Open a pull request: ${args}`,
  },
];

export function filterSlashCommands(query: string | null | undefined): SlashCommand[] {
  if (query == null) return [];
  const q = query.toLowerCase();
  return SLASH_COMMANDS.filter((command) => command.name.startsWith(q));
}

export function highlightedSlashCommands(): SlashCommand[] {
  return SLASH_COMMANDS.filter((command) => command.highlight);
}

/** Known command at the start of a message. Unknown `/tokens` return null so they send as plain text. */
export function parseSlashCommand(text: string): { command: SlashCommand; args: string } | null {
  const match = text.match(/^\/([A-Za-z0-9_-]+)(?:\s+([\s\S]*))?$/);
  if (!match) return null;
  const command = SLASH_COMMANDS.find((entry) => entry.name === match[1].toLowerCase());
  if (!command) return null;
  return { command, args: match[2]?.trim() ?? '' };
}
